import { TFunction } from 'i18next';
import { Linking } from 'react-native';
import { Float } from 'react-native/Libraries/Types/CodegenTypes';
import { Android, iOS } from 'services/forceUpdate/constants';
import {
  AppUpdates,
  OSBanner,
  OSDialog,
  UnsupportedOSPlatform,
} from 'services/forceUpdate/types';
import { SystemBannerType } from 'shared/components/vxl/SystemBanner/SystemBanner';
import {
  isAndroid,
  isIOS,
  isWeb,
  platformVersion,
} from 'shared/constants/platform';

interface SupportedVersion {
  targetVersion: string;
  minVersion: string;
  title: string;
  message: string;
}

interface UnsupportedDialogDetails {
  title: string;
  message: string;
  primaryActionTitle: string;
  secondaryActionTitle: string;
  shouldDisplay: boolean;
}

const EMPTY_OS_BANNER: OSBanner = {
  title: '',
  message: '',
  actionTitle: '',
  allowClosing: false,
  shouldDisplay: false,
};

const EMPTY_OS_DIALOG: OSDialog = {
  title: '',
  message: '',
  primaryActionTitle: '',
  secondaryActionTitle: '',
  shouldDisplay: false,
};

// Mirrors the fallbacks in mapJsonToAppUpdates without copy, so a missing or
// partial Statsig config (e.g. offline cold launch) never blocks or crashes.
const DEFAULT_APP_UPDATES: AppUpdates = {
  serviceMaintenance: {
    title: '',
    message: '',
    blockUserAction: false,
    showMessageInApp: false,
  },
  supportedVersions: {
    title: '',
    message: '',
    ios: {
      targetVersion: iOS.TARGET_VERSION,
      minSupportedVersion: iOS.MIN_SUPPORTED_VERSION,
    },
    android: {
      targetVersion: Android.TARGET_VERSION,
      minSupportedVersion: Android.MIN_SUPPORTED_VERSION,
    },
  },
  unsupportedOS: {
    ios: {
      version: iOS.UNSUPPORTED_VERSION,
      banner: EMPTY_OS_BANNER,
      dialog: EMPTY_OS_DIALOG,
    },
    android: {
      version: Android.UNSUPPORTED_VERSION,
      banner: EMPTY_OS_BANNER,
      dialog: EMPTY_OS_DIALOG,
    },
  },
};

const withUnsupportedOSDefaults = (
  defaults: UnsupportedOSPlatform,
  platform?: Partial<UnsupportedOSPlatform>,
): UnsupportedOSPlatform => ({
  version: platform?.version ?? defaults.version,
  banner: { ...defaults.banner, ...platform?.banner },
  dialog: { ...defaults.dialog, ...platform?.dialog },
});

let dynamicConfig: AppUpdates = DEFAULT_APP_UPDATES;

export function setDynamicData(appUpdates: AppUpdates) {
  dynamicConfig = {
    serviceMaintenance: {
      ...DEFAULT_APP_UPDATES.serviceMaintenance,
      ...appUpdates.serviceMaintenance,
    },
    supportedVersions: {
      ...DEFAULT_APP_UPDATES.supportedVersions,
      ...appUpdates.supportedVersions,
      ios: {
        ...DEFAULT_APP_UPDATES.supportedVersions.ios,
        ...appUpdates.supportedVersions?.ios,
      },
      android: {
        ...DEFAULT_APP_UPDATES.supportedVersions.android,
        ...appUpdates.supportedVersions?.android,
      },
    },
    unsupportedOS: {
      ios: withUnsupportedOSDefaults(
        DEFAULT_APP_UPDATES.unsupportedOS.ios,
        appUpdates.unsupportedOS?.ios,
      ),
      android: withUnsupportedOSDefaults(
        DEFAULT_APP_UPDATES.unsupportedOS.android,
        appUpdates.unsupportedOS?.android,
      ),
    },
  };
}

export function openSettings(): void {
  if (isIOS) {
    Linking.openURL('App-Prefs:root=General').catch(() => {
      console.error('Unable to open settings');
    });
  } else if (isAndroid) {
    Linking.sendIntent('android.settings.SETTINGS').catch(() => {
      console.error('Unable to open settings');
    });
  }
}

// This method is only use for checking support for mobile platform not for web
export function getSupportedVersion(): SupportedVersion {
  if (isAndroid) {
    return {
      targetVersion: dynamicConfig.supportedVersions.android.targetVersion,
      minVersion: dynamicConfig.supportedVersions.android.minSupportedVersion,
      title: dynamicConfig.supportedVersions.title,
      message: dynamicConfig.supportedVersions.message,
    };
  }
  return {
    targetVersion: dynamicConfig.supportedVersions.ios.targetVersion,
    minVersion: dynamicConfig.supportedVersions.ios.minSupportedVersion,
    title: dynamicConfig.supportedVersions.title,
    message: dynamicConfig.supportedVersions.message,
  };
}

const isLatestLessThanCurrent = (latest: string, current: string) => {
  const [latestMajor, latestMinor, latestPatch] = latest.split('.').map(Number);
  const [currentMajor, currentMinor, currentPatch] = current
    .split('.')
    .map(Number);

  if (latestMajor !== currentMajor) {
    return latestMajor < currentMajor;
  }
  if (latestMinor !== currentMinor) {
    return latestMinor < currentMinor;
  }
  return latestPatch < currentPatch;
};

/*
 * appVersion is just for test cases not for actual use, nativeApplicationVersion will handle functionality
 * Suppose valid range is 1.0.0 to 2.0.0
 * Case1: if appVersion is below range i.e 0.9.9 then we are blocking user and showing force update screen
 * Case2: if appVersion is in range i.e 1.9.9 then we are not blocking user and showing force update screen with Ok button
 * Case3: if appVersion is above range i.e 2.9.9 then we are navigating to Login
 */

export function isUpdateRequired(appVersion: string) {
  if (isWeb) {
    return { isUpdateRequired: false };
  }
  const applicationVersion = appVersion;

  // Check for the app version
  const supportedVersion = getSupportedVersion();
  const targetVersion = supportedVersion.targetVersion;
  const minVersion = supportedVersion.minVersion;
  const updateDueToMin = isLatestLessThanCurrent(
    applicationVersion,
    minVersion,
  );
  const updateDueToTarget = isLatestLessThanCurrent(
    applicationVersion,
    targetVersion,
  );
  return {
    isUpdateNeeded: updateDueToMin || updateDueToTarget,
    dueToMinSupportedVersion: updateDueToMin,
    dueToTargetSupportedVersion: updateDueToTarget,
  };
}

export function isServiceMaintenance() {
  return dynamicConfig.serviceMaintenance.showMessageInApp;
}

// This method is only use for checking support for mobile platform not for web
export function isPlatformSupported() {
  let unsupportedVersion: Float;
  if (isAndroid) {
    unsupportedVersion = dynamicConfig.unsupportedOS.android.version;
  } else if (isIOS) {
    unsupportedVersion = dynamicConfig.unsupportedOS.ios.version;
  } else {
    // Returning false for other than mobile platforms
    return false;
  }
  const version = parseFloat('' + platformVersion);
  return version > unsupportedVersion;
}

export function shouldShowBannerDetails() {
  if (!isPlatformSupported() && !isWeb) {
    const unsupportedBannerDetails = getUnsupportedBannerDetails();
    if (unsupportedBannerDetails) {
      return unsupportedBannerDetails.shouldDisplay;
    }
  }
  return false;
}

export function shouldShowDialogDetails() {
  if (!isPlatformSupported() && !isWeb) {
    return getUnsupportedDialogDetails().shouldDisplay;
  }
  return false;
}

export function getUnsupportedBannerDetails() {
  const unsupportedOS = dynamicConfig.unsupportedOS;
  if (isAndroid) {
    const unsupportedPlatformBanner = unsupportedOS.android.banner;
    return {
      titleText: unsupportedPlatformBanner.title,
      bodyText: unsupportedPlatformBanner.message,
      actionTitle: unsupportedPlatformBanner.actionTitle,
      allowClosing: unsupportedPlatformBanner.allowClosing,
      shouldDisplay: unsupportedPlatformBanner.shouldDisplay,
      bannerType: SystemBannerType.INFO,
      iconKeyType: 'INFO',
    };
  }
  const unsupportedPlatformBanner = unsupportedOS.ios.banner;
  return {
    titleText: unsupportedPlatformBanner.title,
    bodyText: unsupportedPlatformBanner.message,
    actionTitle: unsupportedPlatformBanner.actionTitle,
    allowClosing: unsupportedPlatformBanner.allowClosing,
    shouldDisplay: unsupportedPlatformBanner.shouldDisplay,
    bannerType: SystemBannerType.INFO,
    iconKeyType: 'INFO',
  };
}

export function getUnsupportedDialogDetails(): UnsupportedDialogDetails {
  const unsupportedOS = dynamicConfig.unsupportedOS;
  if (isAndroid) {
    const unsupportedPlatformDialog = unsupportedOS.android.dialog;
    return {
      title: unsupportedPlatformDialog.title,
      message: unsupportedPlatformDialog.message,
      primaryActionTitle: unsupportedPlatformDialog.primaryActionTitle,
      secondaryActionTitle: unsupportedPlatformDialog.secondaryActionTitle,
      shouldDisplay: unsupportedPlatformDialog.shouldDisplay,
    };
  }
  const unsupportedPlatformDialog = unsupportedOS.ios.dialog;
  return {
    title: unsupportedPlatformDialog.title,
    message: unsupportedPlatformDialog.message,
    primaryActionTitle: unsupportedPlatformDialog.primaryActionTitle,
    secondaryActionTitle: unsupportedPlatformDialog.secondaryActionTitle,
    shouldDisplay: unsupportedPlatformDialog.shouldDisplay,
  };
}

export function getServiceDownDetails() {
  return {
    title: dynamicConfig.serviceMaintenance.title,
    message: dynamicConfig.serviceMaintenance.message,
    blockUserAction: dynamicConfig.serviceMaintenance.blockUserAction,
    showMessageInApp: dynamicConfig.serviceMaintenance.showMessageInApp,
  };
}

export function mapJsonToAppUpdates(
  json: AppUpdates,
  translatedText: TFunction<string, string>,
): AppUpdates {
  return {
    serviceMaintenance: {
      title:
        json.serviceMaintenance?.title ||
        translatedText('serviceMaintenance.title'),
      message:
        json.serviceMaintenance?.message ||
        translatedText('serviceMaintenance.message'),
      blockUserAction: json.serviceMaintenance?.blockUserAction || false,
      showMessageInApp: json.serviceMaintenance?.showMessageInApp || false,
    },
    supportedVersions: {
      title:
        json.supportedVersions?.title ||
        translatedText('supportedVersions.title'),
      message:
        json.supportedVersions?.message ||
        translatedText('supportedVersions.message'),
      ios: {
        targetVersion:
          json.supportedVersions?.ios?.targetVersion || iOS.TARGET_VERSION,
        minSupportedVersion:
          json.supportedVersions?.ios?.minSupportedVersion ||
          iOS.MIN_SUPPORTED_VERSION,
      },
      android: {
        targetVersion:
          json.supportedVersions?.android?.targetVersion ||
          Android.TARGET_VERSION,
        minSupportedVersion:
          json.supportedVersions?.android?.minSupportedVersion ||
          Android.MIN_SUPPORTED_VERSION,
      },
    },
    unsupportedOS: {
      ios: {
        version: json.unsupportedOS?.ios?.version || iOS.UNSUPPORTED_VERSION,
        banner: {
          title:
            json.unsupportedOS?.ios.banner?.title ||
            translatedText('unsupportedOS.ios.banner.title'),
          message:
            json.unsupportedOS?.ios?.banner?.message ||
            translatedText('unsupportedOS.ios.banner.message', {
              version:
                json.unsupportedOS?.ios?.version || iOS.UNSUPPORTED_VERSION,
            }),
          actionTitle:
            json.unsupportedOS?.ios?.banner?.actionTitle ||
            translatedText('unsupportedOS.ios.banner.actionTitle'),
          allowClosing: json.unsupportedOS?.ios?.banner?.allowClosing || false,
          shouldDisplay:
            json.unsupportedOS?.ios?.banner?.shouldDisplay || false,
        },
        dialog: {
          title:
            json.unsupportedOS?.ios?.dialog?.title ||
            translatedText('unsupportedOS.ios.dialog.title'),
          message:
            json.unsupportedOS?.ios?.dialog?.message ||
            translatedText('unsupportedOS.ios.dialog.message', {
              version:
                json.unsupportedOS?.ios?.version || iOS.UNSUPPORTED_VERSION,
            }),
          primaryActionTitle:
            json.unsupportedOS?.ios?.dialog?.primaryActionTitle ||
            translatedText('unsupportedOS.ios.dialog.primaryActionTitle'),
          secondaryActionTitle:
            json.unsupportedOS?.ios?.dialog?.secondaryActionTitle ||
            translatedText('unsupportedOS.ios.dialog.secondaryActionTitle'),
          shouldDisplay:
            json.unsupportedOS?.ios?.dialog?.shouldDisplay || false,
        },
      },
      android: {
        version:
          json.unsupportedOS?.android?.version || Android.UNSUPPORTED_VERSION,
        banner: {
          title:
            json.unsupportedOS?.android?.banner?.title ||
            translatedText('unsupportedOS.android.banner.title'),
          message:
            json.unsupportedOS?.android?.banner?.message ||
            translatedText('unsupportedOS.android.banner.message', {
              version:
                json.unsupportedOS?.android?.version ||
                Android.UNSUPPORTED_VERSION,
            }),
          actionTitle:
            json.unsupportedOS?.android?.banner?.actionTitle ||
            translatedText('unsupportedOS.android.banner.actionTitle'),
          allowClosing:
            json.unsupportedOS?.android?.banner?.allowClosing || false,
          shouldDisplay:
            json.unsupportedOS?.android?.banner?.shouldDisplay || false,
        },
        dialog: {
          title:
            json.unsupportedOS?.android?.dialog?.title ||
            translatedText('unsupportedOS.android.dialog.title'),
          message:
            json.unsupportedOS?.android?.dialog?.message ||
            translatedText('unsupportedOS.android.banner.message', {
              version:
                json.unsupportedOS?.android?.version ||
                Android.UNSUPPORTED_VERSION,
            }),
          primaryActionTitle:
            json.unsupportedOS?.android?.dialog?.primaryActionTitle ||
            translatedText('unsupportedOS.android.dialog.primaryActionTitle'),
          secondaryActionTitle:
            json.unsupportedOS?.android?.dialog?.secondaryActionTitle ||
            translatedText('unsupportedOS.android.dialog.secondaryActionTitle'),
          shouldDisplay:
            json.unsupportedOS?.android?.dialog?.shouldDisplay || false,
        },
      },
    },
  };
}
